import { Button, Tabs } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ProductConfirmDialogView } from './product-confirm-dialog.view'
import { ErrorText, TextArea, TextInput } from './product-fields'
import type { Translate, TranslationDraftView, TranslationTabView } from './product-types'

type DraftField = keyof TranslationDraftView

/**
 * Props for {@link ProductTranslationsView}. Drafts, the dirty guard and the
 * save/delete calls live in the page hook.
 *
 * @property selected - The locale whose tab is shown.
 * @property draft - The selected tab's editable text.
 * @property errors - Translated field messages of the selected tab.
 * @property dialog - `discard` (leaving a dirty tab) or `delete`, if open.
 * @property canDelete - True with `product.delete`; Delete shows only on a stored translation.
 */
export interface ProductTranslationsViewProps {
	t: Translate
	tabs: TranslationTabView[]
	selected: string
	draft: TranslationDraftView
	errors: Record<string, string>
	formError: string | null
	readOnly: boolean
	canDelete: boolean
	isBusy: boolean
	dialog: 'discard' | 'delete' | null
	dialogError: string | null
	onSelect: (locale: string) => void
	onChange: (field: DraftField, value: string) => void
	onSave: () => void
	onDelete: () => void
	onConfirmDelete: () => void
	onStay: () => void
	onDiscard: () => void
	onCancelDelete: () => void
}

const tabLabel = (t: Translate, tab: TranslationTabView) =>
	[tab.locale, tab.required ? t('translations.required') : null, tab.missing ? t('translations.missing') : null]
		.filter(Boolean)
		.join(' · ')

function DraftForm({ props }: { props: ProductTranslationsViewProps }): ReactElement {
	const { t, draft, errors, readOnly, onChange } = props
	const tab = props.tabs.find((x) => x.locale === props.selected)
	const input = (field: DraftField, long = false) => {
		const Component = long ? TextArea : TextInput
		return (
			<Component
				label={t(`translations.${field}`)}
				value={draft[field]}
				error={errors[field]}
				disabled={readOnly}
				onChange={(v) => onChange(field, v)}
			/>
		)
	}
	return (
		<div className="flex flex-col gap-3">
			{input('name')}
			{input('tagline')}
			{input('shortDescription', true)}
			{input('longDescription', true)}
			{input('benefits', true)}
			{input('mainCtaLabel')}
			{input('secondaryCtaLabel')}
			<ErrorText message={props.formError} />
			<div className="flex gap-2">
				{readOnly ? null : (
					<Button isDisabled={props.isBusy} onPress={props.onSave}>
						{t('translations.save')}
					</Button>
				)}
				{props.canDelete && tab?.stored ? (
					<Button variant="danger" isDisabled={props.isBusy} onPress={props.onDelete}>
						{t('translations.delete')}
					</Button>
				) : null}
			</div>
		</div>
	)
}

/** Locale tabs with the per-locale translation form, the discard guard and delete (organism). */
export function ProductTranslationsView(props: ProductTranslationsViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('translations.title')}</h2>
			<Tabs
				selectedKey={props.selected}
				onSelectionChange={(key) => props.onSelect(String(key))}
				tabs={props.tabs.map((tab) => ({
					id: tab.locale,
					label: tabLabel(t, tab),
					content: tab.locale === props.selected ? <DraftForm props={props} /> : null,
				}))}
			/>
			<ProductConfirmDialogView
				t={t}
				isOpen={props.dialog === 'discard'}
				title={t('translations.discardTitle')}
				body={t('translations.discardBody', { locale: props.selected })}
				confirmLabel={t('translations.discard')}
				cancelLabel={t('translations.stay')}
				danger
				onConfirm={props.onDiscard}
				onCancel={props.onStay}
			/>
			<ProductConfirmDialogView
				t={t}
				isOpen={props.dialog === 'delete'}
				title={t('translations.deleteTitle')}
				body={t('translations.deleteBody', { locale: props.selected })}
				confirmLabel={t('translations.delete')}
				danger
				error={props.dialogError}
				onConfirm={props.onConfirmDelete}
				onCancel={props.onCancelDelete}
			/>
		</section>
	)
}
